import { useRef, useState, type PointerEvent, type ReactNode } from 'react';

/** How far a row has to travel before letting go fires the swipe. */
const SWIPE_THRESHOLD = 80;

/**
 * Horizontal touch swipe on a row; the page still scrolls vertically
 * (`touch-action: pan-y` on the row). Mouse and pen are ignored, so desktop
 * clicks behave as usual. `consumeClick` swallows the click a browser fires
 * after a swipe ends on the row.
 */
function useSwipe(onLeft: () => void, onRight: () => void) {
  const [offset, setOffset] = useState(0);
  const start = useRef<{ x: number; y: number } | null>(null);
  const axis = useRef<'x' | 'y' | null>(null);
  const swiped = useRef(false);

  const reset = () => {
    start.current = null;
    axis.current = null;
    setOffset(0);
  };

  return {
    offset,
    consumeClick: () => {
      const was = swiped.current;
      swiped.current = false;
      return was;
    },
    handlers: {
      onPointerDown: (e: PointerEvent<HTMLElement>) => {
        if (e.pointerType !== 'touch') return;
        start.current = { x: e.clientX, y: e.clientY };
        axis.current = null;
      },
      onPointerMove: (e: PointerEvent<HTMLElement>) => {
        if (!start.current) return;
        const dx = e.clientX - start.current.x;
        const dy = e.clientY - start.current.y;
        if (axis.current === null) {
          if (Math.abs(dy) > 10) axis.current = 'y';
          else if (Math.abs(dx) > 10) {
            axis.current = 'x';
            e.currentTarget.setPointerCapture(e.pointerId);
          }
        }
        if (axis.current === 'x') setOffset(dx);
      },
      onPointerUp: () => {
        if (axis.current === 'x') {
          swiped.current = true;
          if (offset <= -SWIPE_THRESHOLD) onLeft();
          else if (offset >= SWIPE_THRESHOLD) onRight();
        }
        reset();
      },
      onPointerCancel: reset,
    },
  };
}

/**
 * A list row that a finger can swipe: right runs the row's quick action,
 * left deletes it. A tap (or a click on desktop) runs `onClick` instead.
 */
export function SwipeRow({
  className,
  swipingClassName,
  bodyClassName,
  startLabel,
  deleteLabel,
  onRight,
  onLeft,
  onClick,
  children,
}: {
  className: string;
  /** Added to the row only while it is being swiped. */
  swipingClassName?: string;
  bodyClassName: string;
  /** Uncovered by a right swipe: what letting go would do. */
  startLabel: ReactNode;
  /** Uncovered by a left swipe. */
  deleteLabel: ReactNode;
  onRight: () => void;
  onLeft: () => void;
  onClick: () => void;
  children: ReactNode;
}) {
  const swipe = useSwipe(onLeft, onRight);
  return (
    <li
      className={
        swipe.offset !== 0 && swipingClassName
          ? `${className} ${swipingClassName}`
          : className
      }
      {...swipe.handlers}
    >
      {swipe.offset > 0 && (
        <div className="swipe-under swipe-under-start" aria-hidden="true">
          {startLabel}
        </div>
      )}
      {swipe.offset < 0 && (
        <div className="swipe-under swipe-under-delete" aria-hidden="true">
          {deleteLabel}
        </div>
      )}
      <div
        className={
          swipe.offset !== 0 ? bodyClassName : `${bodyClassName} swipe-settle`
        }
        style={
          swipe.offset === 0
            ? undefined
            : { transform: `translateX(${swipe.offset}px)` }
        }
        onClick={() => {
          if (!swipe.consumeClick()) onClick();
        }}
      >
        {children}
      </div>
    </li>
  );
}
